import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
    Box, Typography, Button, TextField,
    Tooltip, Paper, Chip, Card, CardContent, Divider,
    IconButton, useTheme, useMediaQuery, CircularProgress,
    ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
    Search, Add, Edit, Delete, Visibility,
} from '@mui/icons-material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';

import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { DataTableWithStaticSchema } from 'components/DataTable';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import {
    SWIPE_ACTION_WIDTH_PX,
    SWIPE_DIRECTION_LOCK_PX,
    clampSwipeOffset,
    computeSnappedOffset,
} from 'utils/iosSwipeRow';
import { DomainDialog } from './DomainDialog';
import { DomainViewDialog } from './DomainViewDialog';

interface DomainRecord {
    _id: string;
    domainKey: string;
    apiBaseUrl: string;
    brandLogo: { name: string; path: string };
    minifiedBrandLogo: { name: string; path: string };
    themeBrandLogos?: { hasLight: boolean; hasDark: boolean };
    menuKey: string;
    theme?: string;
    uiDictionaryKey?: 'campaignKing' | 'instantUpdate' | 'anamnesis' | 'infralytiqs';
    supportEmail: string;
    contactPhone: string;
    contactEmail: string;
    logLevel: string;
    facebookAppId: string;
    facebookApiVersion: string;
    fbEmbeddedRedirectUri: string;
    googleMapsBrowserJSApiKey: string;
    loginPageHashPath: string;
    loginSignInTermsNotice?: string;
    userSignUpEnabled?: boolean;
    ssoSignUpEnabled?: boolean;
    ssoSignUp?: {
        licenseType?: string;
        module?: string;
        duration?: string;
    };
    assetsS3CdnUrl?: string;
    createdBy?: string;
    modifiedBy?: string;
    createdAt?: string;
    updatedAt?: string;
}

const VIEWMODE_STORAGE_KEY = 'domains-view-mode';
const TILE_PAGE_SIZE = 12;

const tableSchema = {
    paths: {
        _id: { instance: 'String', isRequired: false },
        domainKey: { instance: 'String', isRequired: true },
        apiBaseUrl: { instance: 'String', isRequired: true },
        menuKey: { instance: 'String', isRequired: true },
        logLevel: { instance: 'String', isRequired: false },
    },
    obj: {},
};

export const Domains: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
        const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
        return stored === 'table' ? 'table' : 'tile';
    });

    const [domains, setDomains] = useState<DomainRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDomain, setEditingDomain] = useState<DomainRecord | null>(null);

    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [viewingDomain, setViewingDomain] = useState<DomainRecord | null>(null);

    const [tileVisibleCount, setTileVisibleCount] = useState(TILE_PAGE_SIZE);

    const [tileSwipeRow, setTileSwipeRow] = useState<{
        tileId: string; offset: number; dragging: boolean;
    } | null>(null);
    const swipeSessionRef = useRef<{
        tileId: string; startX: number; startY: number; baseOffset: number; lock: 'h' | 'v' | null;
    } | null>(null);
    const latestSwipeOffsetRef = useRef(0);

    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

    const fetchDomains = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/domain/all');
            setDomains(response.data || []);
        } catch (error) {
            Logger.error('Domains', `Failed to fetch domains: ${error}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDomains();
    }, [fetchDomains]);

    const filteredDomains = useMemo(() => {
        if (!searchQuery.trim()) return domains;
        const q = searchQuery.toLowerCase();
        return domains.filter(d =>
            d.domainKey.toLowerCase().includes(q) ||
            d.apiBaseUrl.toLowerCase().includes(q) ||
            d.menuKey.toLowerCase().includes(q)
        );
    }, [domains, searchQuery]);

    useEffect(() => {
        setTileVisibleCount(TILE_PAGE_SIZE);
    }, [searchQuery, domains]);

    const paginatedDomains = useMemo(() => filteredDomains.slice(0, tileVisibleCount), [filteredDomains, tileVisibleCount]);
    const hasMoreTiles = tileVisibleCount < filteredDomains.length;

    const handleCreate = () => { setEditingDomain(null); setDialogOpen(true); };
    const handleView = (domain: DomainRecord) => { setViewingDomain(domain); setViewDialogOpen(true); };
    const handleEdit = (domain: DomainRecord) => { setEditingDomain(domain); setDialogOpen(true); };

    const handleDelete = async (domain: DomainRecord) => {
        if (!window.confirm(`Delete domain "${domain.domainKey}"?`)) return;
        try {
            await apiClient.delete(`/domain/${domain._id}`);
            setRefreshKey(k => k + 1);
            fetchDomains();
        } catch (error) {
            Logger.error('Domains', `Failed to delete domain: ${error}`);
        }
    };

    const handleDialogClose = () => { setDialogOpen(false); setEditingDomain(null); };
    const handleDialogSave = () => { setDialogOpen(false); setEditingDomain(null); setRefreshKey(k => k + 1); fetchDomains(); };

    // --- Swipe handlers (iOS-style) ---
    const handleTileTouchStart = useCallback(
        (e: React.TouchEvent, domain: DomainRecord) => {
            if (!isMobile) return;
            const t = e.touches[0];
            setTileSwipeRow((prev) => {
                const anotherWasOpen = Boolean(prev && prev.tileId !== domain._id && Math.abs(prev.offset) > 0.5);
                const baseOffset = !anotherWasOpen && prev?.tileId === domain._id ? prev.offset : 0;
                swipeSessionRef.current = { tileId: domain._id, startX: t.clientX, startY: t.clientY, baseOffset, lock: null };
                latestSwipeOffsetRef.current = baseOffset;
                return { tileId: domain._id, offset: baseOffset, dragging: true };
            });
        }, [isMobile]
    );

    const handleTileTouchMove = useCallback(
        (e: React.TouchEvent, domain: DomainRecord) => {
            if (!isMobile) return;
            const sess = swipeSessionRef.current;
            if (!sess || sess.tileId !== domain._id) return;
            const touch = e.touches[0];
            const dx = touch.clientX - sess.startX;
            const dy = touch.clientY - sess.startY;
            if (sess.lock === null) {
                if (Math.abs(dx) > SWIPE_DIRECTION_LOCK_PX && Math.abs(dx) > Math.abs(dy) * 1.2) sess.lock = 'h';
                else if (Math.abs(dy) > SWIPE_DIRECTION_LOCK_PX && Math.abs(dy) > Math.abs(dx) * 1.2) sess.lock = 'v';
            }
            if (sess.lock !== 'h') return;
            const raw = sess.baseOffset + dx;
            const clamped = clampSwipeOffset(raw, true, true, SWIPE_ACTION_WIDTH_PX);
            latestSwipeOffsetRef.current = clamped;
            setTileSwipeRow({ tileId: domain._id, offset: clamped, dragging: true });
        }, [isMobile]
    );

    const handleTileTouchEnd = useCallback(
        (e: React.TouchEvent, domain: DomainRecord) => {
            if (!isMobile) return;
            const sess = swipeSessionRef.current;
            if (!sess || sess.tileId !== domain._id) { swipeSessionRef.current = null; return; }
            const t = e.changedTouches[0];
            const dx = t.clientX - sess.startX;
            const dy = t.clientY - sess.startY;
            const { lock, baseOffset } = sess;
            swipeSessionRef.current = null;
            const finishSnap = (off: number) => {
                const snapped = computeSnappedOffset(off, true, true, SWIPE_ACTION_WIDTH_PX);
                latestSwipeOffsetRef.current = snapped;
                setTileSwipeRow({ tileId: domain._id, offset: snapped, dragging: false });
            };
            if (lock === null && Math.abs(dx) > Math.abs(dy) * 1.2 && Math.abs(dx) > 18) {
                finishSnap(clampSwipeOffset(baseOffset + dx, true, true, SWIPE_ACTION_WIDTH_PX));
                return;
            }
            if (lock !== 'h') {
                if (Math.abs(dx) < 12 && Math.abs(dy) < 12) {
                    if (Math.abs(baseOffset) > 0.5) { latestSwipeOffsetRef.current = 0; setTileSwipeRow({ tileId: domain._id, offset: 0, dragging: false }); }
                    else handleView(domain);
                } else { latestSwipeOffsetRef.current = baseOffset; setTileSwipeRow({ tileId: domain._id, offset: baseOffset, dragging: false }); }
                return;
            }
            finishSnap(latestSwipeOffsetRef.current);
        }, [isMobile]
    );

    const handleTileTouchCancel = useCallback(
        (_e: React.TouchEvent, domain: DomainRecord) => {
            if (!isMobile) return;
            const sess = swipeSessionRef.current;
            if (!sess || sess.tileId !== domain._id) return;
            swipeSessionRef.current = null;
            const snapped = computeSnappedOffset(latestSwipeOffsetRef.current, true, true, SWIPE_ACTION_WIDTH_PX);
            latestSwipeOffsetRef.current = snapped;
            setTileSwipeRow({ tileId: domain._id, offset: snapped, dragging: false });
        }, [isMobile]
    );

    const customActions = useCallback((record: any) => {
        return [{ label: 'View Details', onClick: () => handleView(record), color: 'info' as const, icon: <Visibility /> }];
    }, []);

    const renderTileView = () => (
        <Box display="flex" flexDirection="column" gap={2.5}>
            <Box display="flex" flexWrap="wrap" gap={2.5} sx={{ justifyContent: 'center' }}>
                {paginatedDomains.map((domain) => {
                    const rowOffset = isMobile && tileSwipeRow?.tileId === domain._id ? tileSwipeRow.offset : 0;
                    const rowDragging = isMobile && tileSwipeRow?.tileId === domain._id && tileSwipeRow.dragging;

                    if (!isMobile) {
                        return (
                            <Card key={domain._id} elevation={0} onClick={() => handleView(domain)}
                                sx={{
                                    width: 300, border: '1px solid', borderColor: 'divider', borderRadius: 2,
                                    display: 'flex', flexDirection: 'column', transition: 'box-shadow 0.2s',
                                    cursor: 'pointer', '&:hover': { boxShadow: theme.shadows[4] },
                                    '&:hover .domain-tile-actions': { opacity: 1 },
                                }}>
                                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3, wordBreak: 'break-all', mb: 1 }}>
                                        {domain.domainKey}
                                    </Typography>
                                    <Chip label={domain.menuKey} size="small" color="primary" sx={{ fontWeight: 600 }} />
                                    <Divider sx={{ my: 1.5 }} />
                                    {domain.contactEmail && (
                                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                                            <Typography variant="caption" color="text.secondary">Email</Typography>
                                            <Typography variant="body2">{domain.contactEmail}</Typography>
                                        </Box>
                                    )}
                                    {domain.contactPhone && (
                                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                                            <Typography variant="caption" color="text.secondary">Phone</Typography>
                                            <Typography variant="body2">{domain.contactPhone}</Typography>
                                        </Box>
                                    )}
                                </CardContent>
                                <Box className="domain-tile-actions"
                                    sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, px: 2, pb: 1.5, opacity: 0, transition: 'opacity 0.2s' }}>
                                    <Tooltip title="Edit">
                                        <Button color="secondary" variant="contained" size="small"
                                            onClick={(e) => { e.stopPropagation(); handleEdit(domain); }}
                                            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                                            <Edit fontSize="small" />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <Button color="error" variant="contained" size="small"
                                            onClick={(e) => { e.stopPropagation(); handleDelete(domain); }}
                                            sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                                            <Delete fontSize="small" />
                                        </Button>
                                    </Tooltip>
                                </Box>
                            </Card>
                        );
                    }

                    return (
                        <Box key={domain._id}
                            sx={{
                                width: '100%', maxWidth: 400, borderRadius: 2, overflow: 'hidden',
                                position: 'relative', flexShrink: 0, border: '1px solid', borderColor: 'divider',
                                touchAction: 'pan-y', isolation: 'isolate',
                            }}>
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'row', alignItems: 'stretch', zIndex: 0, borderRadius: 2, overflow: 'hidden' }}>
                                <Box sx={{ width: SWIPE_ACTION_WIDTH_PX, flexShrink: 0, bgcolor: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Tooltip title="Edit">
                                        <IconButton size="medium" sx={{ color: 'secondary.contrastText' }}
                                            onClick={(e) => { e.stopPropagation(); setTileSwipeRow(null); handleEdit(domain); }}>
                                            <Edit fontSize="medium" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }} />
                                <Box sx={{ width: SWIPE_ACTION_WIDTH_PX, flexShrink: 0, bgcolor: 'error.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Tooltip title="Delete">
                                        <IconButton size="medium" sx={{ color: 'error.contrastText' }}
                                            onClick={(e) => { e.stopPropagation(); setTileSwipeRow(null); handleDelete(domain); }}>
                                            <Delete fontSize="medium" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                            <Box
                                onTouchStart={(e) => handleTileTouchStart(e, domain)}
                                onTouchMove={(e) => handleTileTouchMove(e, domain)}
                                onTouchEnd={(e) => handleTileTouchEnd(e, domain)}
                                onTouchCancel={(e) => handleTileTouchCancel(e, domain)}
                                sx={{
                                    position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column',
                                    borderRadius: 2, overflow: 'hidden', backgroundColor: 'background.paper',
                                    transform: `translateX(${rowOffset}px)`,
                                    transition: rowDragging ? 'none' : 'transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                    willChange: rowDragging ? 'transform' : 'auto',
                                    boxShadow: rowOffset !== 0 ? 3 : 'none', p: 2,
                                }}>
                                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3, wordBreak: 'break-all', mb: 1 }}>
                                    {domain.domainKey}
                                </Typography>
                                <Chip label={domain.menuKey} size="small" color="primary" sx={{ fontWeight: 600 }} />
                                <Divider sx={{ my: 1.5 }} />
                                {domain.contactEmail && (
                                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                                        <Typography variant="caption" color="text.secondary">Email</Typography>
                                        <Typography variant="body2">{domain.contactEmail}</Typography>
                                    </Box>
                                )}
                                {domain.contactPhone && (
                                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                                        <Typography variant="body2">{domain.contactPhone}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {filteredDomains.length === 0 && !loading && (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
                    <Typography color="text.secondary">
                        {searchQuery ? 'No domains match your search.' : 'No domains found. Create one to get started.'}
                    </Typography>
                </Paper>
            )}

            {filteredDomains.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Showing {paginatedDomains.length} of {filteredDomains.length} domain{filteredDomains.length !== 1 ? 's' : ''}
                    </Typography>
                    {hasMoreTiles && (
                        <Button variant="outlined" color="secondary"
                            onClick={() => setTileVisibleCount(prev => prev + TILE_PAGE_SIZE)}>
                            Load More
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );

    const renderTableView = () => (
        <DataTableWithStaticSchema
            dataUrl="/domain/all"
            schemaObject={tableSchema}
            columnsToExclude={['_id']}
            refreshTrigger={refreshKey}
            externalSearchQuery={searchQuery}
            hideSearch
            onEdit={(record: any) => handleEdit(record)}
            onDelete={(record: any) => handleDelete(record)}
            customActions={customActions}
        />
    );

    const toolbarBgSx = {
        backgroundColor: (t: any) =>
            t.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
    };

    return (
        <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
            <DynamicBreadcrumbs
                items={[
                    { itemPath: '/dashboard', itemLabel: 'Home' },
                    { itemPath: '/administration/domains', itemLabel: 'Domains' },
                ]}
            />

            <Box
                sx={{
                    display: 'flex', flexDirection: 'row', gap: { xs: 1, md: 2 },
                    alignItems: 'center', mb: 3, p: 1.5, borderRadius: 2, ...toolbarBgSx,
                }}>
                {/* Mobile: Search toggle */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                    <IconButton color="secondary" onClick={() => setMobileSearchOpen(prev => !prev)} size="large"
                        sx={{ border: '1px solid', borderColor: 'secondary.main', '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' } }}>
                        <Search />
                    </IconButton>
                </Box>

                {/* Desktop: Search */}
                <Box sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1, maxWidth: 280 }}>
                    <TextField label="Search Domains" variant="outlined" color="secondary" size="small"
                        value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} fullWidth />
                </Box>

                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexGrow: 1 }} />

                {/* Desktop: View Toggle + Create */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, ml: 'auto' }}>
                    <ToggleButtonGroup value={viewMode} exclusive
                        onChange={(_e, newMode) => { if (newMode !== null) { setViewMode(newMode); localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode); } }}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                border: '1px solid', borderColor: 'divider',
                                '&.Mui-selected': {
                                    backgroundColor: (t) => t.palette.mode === 'dark' ? '#191f45' : 'secondary.main',
                                    color: (t) => t.palette.mode === 'dark' ? '#fff' : 'secondary.contrastText',
                                    '&:hover': { backgroundColor: (t) => t.palette.mode === 'dark' ? '#232a54' : 'secondary.dark' },
                                },
                            },
                        }}>
                        <ToggleButton value="tile" aria-label="Tile view">
                            <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
                        </ToggleButton>
                        <ToggleButton value="table" aria-label="Table view">
                            <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Button variant="outlined" color="secondary" startIcon={<Add />} onClick={handleCreate}
                        size="small" sx={{ whiteSpace: 'nowrap' }}>
                        Create Domain
                    </Button>
                </Box>

                {/* Mobile: Create */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                    <IconButton color="secondary" onClick={handleCreate} size="large"
                        sx={{ border: '1px solid', borderColor: 'secondary.main', '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' } }}>
                        <Add />
                    </IconButton>
                </Box>
            </Box>

            {mobileSearchOpen && (
                <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
                    <TextField label="Search Domains" variant="outlined" color="secondary" size="small"
                        fullWidth autoFocus value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </Box>
            )}

            {loading && domains.length === 0 ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress color="secondary" />
                </Box>
            ) : viewMode === 'tile' ? renderTileView() : renderTableView()}

            {dialogOpen && (
                <DomainDialog open={dialogOpen} onClose={handleDialogClose} onSave={handleDialogSave} domain={editingDomain} />
            )}

            <DomainViewDialog open={viewDialogOpen} onClose={() => { setViewDialogOpen(false); setViewingDomain(null); }} domain={viewingDomain} />
        </Box>
    );
};

export default Domains;
